import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

// Componentes
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs';
import FriendlyPhrase from '../../components/FriendlyPhrase/FriendlyPhrase';

// Contextos
import { useToast } from '../../contexts/ToastContext';
import { useLoader } from '../../contexts/LoaderContext';

// API
import { useProdutoService } from '../../services/produtoService';
import { useVideoService } from '../../services/videoService';

// Constantes
import { URLS } from '../../constants/urls';

// Estilos
import styles from './videoformpage.module.css';

function VideoFormPage() {
    const { showToast } = useToast();
    const { showLoader, hideLoader } = useLoader();
    const navigate = useNavigate();
    const { idVideo } = useParams();
    const { pathname } = useLocation();

    const isEditing = !!idVideo && pathname.includes('atualizar');

    const { listarProdutos } = useProdutoService();
    const { criarVideo, atualizarVideo, obterVideoPorId } = useVideoService();

    const [produtos, setProdutos] = useState([]);
    const [buscaProduto, setBuscaProduto] = useState('');
    const [sugestoesFiltradas, setSugestoesFiltradas] = useState([]);

    const [formData, setFormData] = useState({
        nome: '',
        descricao: '',
        produtos: [],
        video: null,
        thumbnail: null
    });

    const [previewVideo, setPreviewVideo] = useState(null);
    const [previewThumbnail, setPreviewThumbnail] = useState(null);

    useEffect(() => {
        const carregarProdutos = async () => {
            showLoader('Carregando produtos');
            const response = await listarProdutos();
            if (response.success) {
                setProdutos(response.data);
            } else {
                showToast("Erro ao carregar produtos", 3000, 'error');
            }
            hideLoader();
        };

        carregarProdutos();
    }, []);

    useEffect(() => {
        if (isEditing && produtos.length > 0) {
            const carregarVideo = async () => {
                showLoader("Carregando vídeo");

                const res = await obterVideoPorId(idVideo);
                if (res.success) {
                    const v = res.video;
                    
                    const produtosRelacionados = produtos
                        .filter(p => v.inProducts.includes(p.nome))
                        .map(p => p.id);


                    setFormData({
                        nome: v.name,
                        descricao: v.description,
                        produtos: produtosRelacionados,
                        video: null,
                        thumbnail: null
                    });

                    setPreviewVideo(`${URLS.IMG_PATH}${v.link}`);
                    setPreviewThumbnail(`${URLS.IMG_PATH}${v.thumbnail}`);
                } else {
                    showToast("Erro ao carregar vídeo", 3000, 'error');
                    navigate('/videos');
                }

                hideLoader();
            };

            carregarVideo();
        }
    }, [isEditing, produtos]);

    const handleFileChange = (e, tipo) => {
        const file = e.target.files[0];

        if (!file) return;

        setFormData(prev => ({ ...prev, [tipo]: file }));

        const url = URL.createObjectURL(file);

        if (tipo === 'video') {
            setPreviewVideo(null);
            setTimeout(() => setPreviewVideo(url), 0);
        }

        if (tipo === 'thumbnail') setPreviewThumbnail(url);
    };

    const handleProdutoSelect = (produto) => {
        setFormData(prev => ({
            ...prev,
            produtos: [...prev.produtos, produto.id]
        }));
        setBuscaProduto('');
        setSugestoesFiltradas([]);
    };

    const handleProdutoRemove = (id) => {
        setFormData(prev => ({
            ...prev,
            produtos: prev.produtos.filter(pid => pid !== id)
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        showLoader(isEditing ? "Atualizando vídeo" : "Criando vídeo");

        try {
            const payload = new FormData();
            payload.append('nome', formData.nome);
            payload.append('descricao', formData.descricao);

            formData.produtos.forEach((produtoId) => {
                payload.append('idsProdutos[]', produtoId);
            });

            if (formData.video) {
                payload.append('video', formData.video);
            } else {
                console.warn("⚠️ Nenhum vídeo selecionado");
            }

            if (formData.thumbnail) {
                payload.append('thumbnail', formData.thumbnail);
            } else {
                console.warn("⚠️ Nenhuma thumbnail selecionada");
            }

            if (isEditing) {
                payload.append('id', idVideo);
            }

            console.log("🎥 Vídeo selecionado:", formData.video);

            const response = isEditing
                ? await atualizarVideo(payload)
                : await criarVideo(payload);

            if (response.success) {
                showToast(isEditing ? "Vídeo atualizado com sucesso!" : "Vídeo criado com sucesso!", 3000, 'success');
                navigate('/videos');
            } else {
                showToast(response.message || "Erro ao salvar vídeo", 4000, 'error');
            }

        } catch (error) {
            showToast("Erro ao conectar com o servidor", 4000, 'error');
        } finally {
            hideLoader();
        }
    };

    useEffect(() => {
        const filtrados = produtos.filter(p =>
            !formData.produtos.includes(p.id) &&
            p.nome.toLowerCase().includes(buscaProduto.toLowerCase())
        );
        setSugestoesFiltradas(filtrados);
    }, [buscaProduto, produtos, formData.produtos]);

    const breadcrumbLabel = isEditing
        ? `${formData.nome || 'video'}`
        : undefined;

    return (
        <div className={styles.VideoFormPage}>
            <div className={styles.containerTop}>
                <Breadcrumbs idLabels={breadcrumbLabel ? [{ id: idVideo, label: breadcrumbLabel }] : []} />

                <h1 className={styles.title}>{isEditing ? 'Editar Vídeo' : 'Cadastro de Vídeo'}</h1>
                <FriendlyPhrase page={isEditing ? 'atualizarVideo' : 'adicionarVideo'} />
            </div>

            <form onSubmit={handleSubmit} className={styles.formulario} enctype="multipart/form-data">
                {/* Produtos */}
                <div className={styles.produtoSelector}>
                    <h2 className={styles.sectionTitle}>Vincular Produtos</h2>

                    <div className={styles.produtoPills}>
                        {formData.produtos.map(id => {
                            const prod = produtos.find(p => p.id === id);
                            return (
                                <div key={id} className={styles.produtoPill}>
                                    {prod?.nome}
                                    <span className={styles.removerPill} onClick={() => handleProdutoRemove(id)}>×</span>
                                </div>
                            );
                        })}
                    </div>

                    <input
                        type="text"
                        placeholder="Buscar produtos"
                        value={buscaProduto}
                        onChange={e => setBuscaProduto(e.target.value)}
                        className={styles.produtoInput}
                    />

                    {buscaProduto.trim() !== '' && sugestoesFiltradas.length > 0 && (
                        <ul className={styles.sugestoesLista}>
                            {sugestoesFiltradas.map(prod => (
                                <li
                                    key={prod.id}
                                    className={styles.sugestaoItem}
                                    onClick={() => handleProdutoSelect(prod)}
                                >
                                    {prod.nome}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className={styles.generalArea}>
                    <h2 className={styles.sectionTitle}>Dados do Vídeo</h2>

                    <label>
                        Nome:
                        <input
                            type="text"
                            name="nome"
                            value={formData.nome}
                            onChange={e => setFormData({ ...formData, nome: e.target.value })}
                            required
                        />
                    </label>

                    <label>
                        Descrição:
                        <textarea
                            name="descricao"
                            value={formData.descricao}
                            onChange={e => setFormData({ ...formData, descricao: e.target.value })}
                            required
                        />
                    </label>

                    {/* Vídeo */}
                    <label>
                        Vídeo:
                        <div className={styles.fileInputWrapper}>
                            <span className={styles.fileLabel}>Selecionar Vídeo</span>
                            <input
                                type="file"
                                accept="video/*"
                                className={styles.fileInput}
                                onChange={(e) => handleFileChange(e, 'video')}
                                required={!isEditing}
                            />
                        </div>
                    </label>

                    {previewVideo && (
                        <video controls className={styles.videoPreview}>
                            <source src={previewVideo} type="video/mp4" />
                            Seu navegador não suporta vídeo.
                        </video>
                    )}

                    {/* Thumbnail */}
                    
                    <label>
                        Thumbnail:
                        <div className={styles.fileInputWrapper}>
                            <input
                                type="file"
                                accept="image/*"
                                className={styles.fileInput}
                                onChange={(e) => handleFileChange(e, 'thumbnail')}
                                required={!isEditing}
                            />
                            <span className={styles.fileLabel}>Selecionar Imagem</span>
                        </div>
                    </label>

                    {previewThumbnail && (
                        <img src={previewThumbnail} alt="Preview da Thumbnail" className={styles.logoPreview} />
                    )}
                </div>

                <div className={styles.submitArea}>
                    <button type="submit" className={styles.submitButton}>
                        {isEditing ? 'Atualizar Vídeo' : 'Salvar Vídeo'}
                    </button>
                </div>
            </form>

        </div>
    );
}

export default VideoFormPage;